'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetUserActivityRow, useGetPublicPartnerCreator, useGetUserActivityInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { PortfolioAppUpsellCard } from '@/components/app-upsells/portfolio-app-upsell-card';
import { BackedProposalsPanel } from '@/components/portfolio/backed-proposals/backed-proposals-panel';
import { BackerRewardsCard } from '@/components/portfolio/cards/backer-rewards-card';
import { PortfolioPnlCard } from '@/components/portfolio/cards/portfolio-pnl-card';
import { ProfilePortfolioCard } from '@/components/portfolio/cards/profile-portfolio-card';
import { CreatedPanel } from '@/components/portfolio/created/created-panel';
import { ActivityEmpty } from '@/components/portfolio/empty-states/activity-empty';
import { FaucetBanner } from '@/components/portfolio/faucet-banner';
import { useResolvedSelection } from '@/components/portfolio/header/use-resolved-selection';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { PortfolioPositionsPanel } from '@/components/portfolio/positions-panel/portfolio-positions-panel';
import { ReferralsPanel } from '@/components/portfolio/referrals/referrals-panel';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { GiftsSummaryCard } from '@/components/portfolio/summary-cards/gifts-summary-card';
import { PortfolioActivityFeed } from '@/components/portfolio/tabs/portfolio-activity-feed';
import { PortfolioThesesPanel } from '@/components/portfolio/tabs/portfolio-theses-panel';
import { UnauthenticatedPrompt } from '@/components/portfolio/unauthenticated-prompt';
import { StreakWidget } from '@/components/streaks/streak-widget';
import { Panel, PanelContent } from '@/components/ui/panel';
import { UserPortfolioSnapshotQueryOwner } from '@/hooks/positions/user-portfolio/query-owner';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { checkGate } from '@/lib/analytics';
import { ACTIVITY_FEED_FIELDS } from '@/lib/sdk/projections';
import { isActivityAttributedToWallet } from '@/lib/wallet/multi-wallet-scope';
import type { Wallet } from '@/types/auth';
import { PartnerLoadError } from './partner/partner-load-error';
import { PartnerVideo } from './partner/partner-video';
interface ConnectedProvider {
  provider: string;
  username?: string;
  displayName?: string;
}
export interface PortfolioBodyProps {
  creatorUserId: string | null;
  registeredAt: string | null;
  /** Active wallets — own variant passes its wallet list, other variant passes []. */
  wallets: Wallet[];
  /**
   * Primary wallet for the viewer. Own viewer passes the auth user's primary
   * wallet. Other viewer has no wallet info — pass null.
   */
  primaryWallet: Wallet | null;
  /** Identity passthrough — header reads these instead of `useAuth()`. */
  displayName: string | null;
  handle: string | null;
  bio: string | null;
  avatarUrl: string | null;
  /** Spec 071 NSFW flag for the avatar URL. */
  avatarNsfw?: boolean | null;
  /** `users.avatar_preferences.rasterise` — drives the avatar render mode. */
  avatarRasterise?: boolean | null;
  heroUrl: string | null;
  /** Spec 071 NSFW flag for the cover URL. */
  coverNsfw?: boolean | null;
  connectedProviders: ConnectedProvider[];
}
export function PortfolioBody(t0) {
  const $ = _c(114);
  const {
    creatorUserId,
    registeredAt,
    wallets,
    primaryWallet,
    displayName,
    handle,
    bio,
    avatarUrl,
    avatarNsfw,
    avatarRasterise,
    heroUrl,
    coverNsfw,
    connectedProviders
  } = t0;
  const {
    viewerMode,
    userId,
    financialQueryId,
    primaryWalletPubkey,
    walletPubkey: contextWalletPubkey,
    walletPubkeys: contextWalletPubkeys,
    multiWalletEnabled
  } = usePortfolioContext();
  const isViewerOwn = viewerMode === "own";
  const partnerEnabled = useFeatureGate("creator_and_platform_markets");
  const t1 = creatorUserId ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      id: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = partnerEnabled === true && creatorUserId !== null;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      enabled: t3,
      retry: false
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const partnerQuery = useGetPublicPartnerCreator(t2, t4);
  const partnerError = partnerQuery.error;
  const missingPartner = partnerError !== null && "httpStatus" in partnerError && partnerError.httpStatus === 404;
  const partnerProfile = partnerEnabled === true ? partnerQuery.data : undefined;
  const {
    mode,
    wallet
  } = useResolvedSelection(wallets);
  const isSingle = multiWalletEnabled ? mode === "single" && wallet !== null : primaryWalletPubkey !== null;
  const activeWallets = wallets.filter(_temp);
  const realtimeWalletPubkey = multiWalletEnabled && isSingle && wallet !== null ? wallet.address : contextWalletPubkey;
  let t5;
  if ($[4] !== contextWalletPubkeys || $[5] !== isSingle || $[6] !== multiWalletEnabled || $[7] !== wallet) {
    t5 = multiWalletEnabled && isSingle && wallet !== null ? [wallet.address] : contextWalletPubkeys;
    $[4] = contextWalletPubkeys;
    $[5] = isSingle;
    $[6] = multiWalletEnabled;
    $[7] = wallet;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const realtimeWalletPubkeys = t5;
  let t6;
  if ($[9] !== isViewerOwn || $[10] !== userId) {
    t6 = isViewerOwn ? [userId] : [];
    $[9] = isViewerOwn;
    $[10] = userId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const realtimeGiftUserIds = t6;
  if (isViewerOwn && activeWallets.length === 0) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <UnauthenticatedPrompt />;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  const streakOn = isViewerOwn;
  let t7;
  if ($[13] !== isViewerOwn) {
    t7 = isViewerOwn && checkGate("gifting");
    $[13] = isViewerOwn;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const giftsOn = t7;
  let t8;
  if ($[15] !== isViewerOwn) {
    t8 = isViewerOwn && checkGate("referrals");
    $[15] = isViewerOwn;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const referralsOn = t8;
  let t9;
  if ($[17] !== missingPartner || $[18] !== partnerEnabled || $[19] !== partnerQuery) {
    t9 = partnerEnabled && partnerQuery.isError && !missingPartner ? <PartnerLoadError onRetry={() => void partnerQuery.refetch()} /> : null;
    $[17] = missingPartner;
    $[18] = partnerEnabled;
    $[19] = partnerQuery;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== contextWalletPubkeys || $[22] !== isViewerOwn || $[23] !== userId) {
    t10 = !isViewerOwn ? <UserPortfolioSnapshotQueryOwner userId={userId} knownWallets={contextWalletPubkeys} /> : null;
    $[21] = contextWalletPubkeys;
    $[22] = isViewerOwn;
    $[23] = userId;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = partnerProfile ? "flex flex-col gap-4" : "grid grid-cols-1 gap-4 md:grid-cols-5";
  const t12 = partnerProfile ? "flex min-w-0 flex-col gap-4 md:flex-row md:items-stretch" : "min-w-0 md:col-span-2";
  let t13;
  if ($[25] !== partnerProfile) {
    t13 = partnerProfile ? {
      partnerProfile
    } : {};
    $[25] = partnerProfile;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = partnerProfile ? "min-w-0 flex-1" : "";
  const t15 = partnerProfile?.displayName ?? displayName;
  const t16 = partnerProfile?.avatarUrl ?? avatarUrl;
  const t17 = avatarNsfw ?? null;
  const t18 = avatarRasterise ?? null;
  const t19 = partnerProfile ? partnerProfile.bannerUrl : heroUrl;
  const t20 = coverNsfw ?? null;
  let t21;
  if ($[27] !== bio || $[28] !== connectedProviders || $[29] !== handle || $[30] !== realtimeWalletPubkeys || $[31] !== t13 || $[32] !== t14 || $[33] !== t15 || $[34] !== t16 || $[35] !== t17 || $[36] !== t18 || $[37] !== t19 || $[38] !== t20) {
    t21 = <ProfilePortfolioCard {...t13} walletPubkeys={realtimeWalletPubkeys} className={t14} displayName={t15} handle={handle} bio={bio} avatarUrl={t16} avatarNsfw={t17} avatarRasterise={t18} heroUrl={t19} coverNsfw={t20} connectedProviders={connectedProviders} />;
    $[27] = bio;
    $[28] = connectedProviders;
    $[29] = handle;
    $[30] = realtimeWalletPubkeys;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== partnerProfile) {
    t22 = partnerProfile?.videoUrl ? <PartnerVideo key={partnerProfile.videoUrl} url={partnerProfile.videoUrl} /> : null;
    $[40] = partnerProfile;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t12 || $[43] !== t21 || $[44] !== t22) {
    t23 = <div className={t12}>{t21}{t22}</div>;
    $[42] = t12;
    $[43] = t21;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== financialQueryId || $[47] !== realtimeWalletPubkey || $[48] !== realtimeWalletPubkeys || $[49] !== registeredAt || $[50] !== userId || $[51] !== viewerMode || $[52] !== wallets) {
    t24 = <div className="min-w-0 md:col-span-3 md:h-full"><PortfolioPnlCard wallets={wallets} registeredAt={registeredAt} viewerMode={viewerMode} userId={financialQueryId} snapshotUserId={userId} walletPubkey={realtimeWalletPubkey} walletPubkeys={realtimeWalletPubkeys} /></div>;
    $[46] = financialQueryId;
    $[47] = realtimeWalletPubkey;
    $[48] = realtimeWalletPubkeys;
    $[49] = registeredAt;
    $[50] = userId;
    $[51] = viewerMode;
    $[52] = wallets;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t11 || $[55] !== t23 || $[56] !== t24) {
    t25 = <div className={t11}>{t23}{t24}</div>;
    $[54] = t11;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== isViewerOwn || $[59] !== primaryWallet || $[60] !== wallets) {
    t26 = isViewerOwn ? <div className="flex flex-col items-stretch gap-4 empty:hidden lg:flex-row"><PortfolioAppUpsellCard /><FaucetBanner wallets={wallets} primaryWallet={primaryWallet} /></div> : null;
    $[58] = isViewerOwn;
    $[59] = primaryWallet;
    $[60] = wallets;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== creatorUserId || $[63] !== displayName || $[64] !== handle || $[65] !== partnerProfile) {
    t27 = partnerProfile !== undefined && creatorUserId !== null ? <CreatedPanel userId={creatorUserId} handle={handle} displayName={displayName} /> : null;
    $[62] = creatorUserId;
    $[63] = displayName;
    $[64] = handle;
    $[65] = partnerProfile;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== realtimeWalletPubkey || $[68] !== realtimeWalletPubkeys || $[69] !== userId) {
    t28 = <PortfolioPositionsPanel userId={realtimeWalletPubkey} snapshotUserId={userId} walletPubkeys={realtimeWalletPubkeys} />;
    $[67] = realtimeWalletPubkey;
    $[68] = realtimeWalletPubkeys;
    $[69] = userId;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  const t29 = isViewerOwn && isSingle ? realtimeWalletPubkey : multiWalletEnabled ? null : primaryWalletPubkey;
  let t30;
  if ($[71] !== isViewerOwn || $[72] !== realtimeWalletPubkeys || $[73] !== t29 || $[74] !== userId) {
    t30 = <BackedProposalsPanel userId={userId} wallet={t29} walletPubkeys={realtimeWalletPubkeys} own={isViewerOwn} />;
    $[71] = isViewerOwn;
    $[72] = realtimeWalletPubkeys;
    $[73] = t29;
    $[74] = userId;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== creatorUserId || $[77] !== displayName || $[78] !== handle || $[79] !== partnerProfile) {
    t31 = partnerProfile === undefined && creatorUserId !== null ? <CreatedPanel userId={creatorUserId} handle={handle} displayName={displayName} /> : null;
    $[76] = creatorUserId;
    $[77] = displayName;
    $[78] = handle;
    $[79] = partnerProfile;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  const t32 = partnerProfile?.displayName ?? displayName;
  let t33;
  if ($[81] !== handle || $[82] !== t32) {
    t33 = <BackerRewardsCard handle={handle} displayName={t32} />;
    $[81] = handle;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== giftsOn || $[85] !== isViewerOwn || $[86] !== referralsOn || $[87] !== userId) {
    t34 = isViewerOwn ? <EngagementCards userId={userId} giftsOn={giftsOn} referralsOn={referralsOn} /> : null;
    $[84] = giftsOn;
    $[85] = isViewerOwn;
    $[86] = referralsOn;
    $[87] = userId;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== multiWalletEnabled || $[90] !== primaryWalletPubkey || $[91] !== realtimeWalletPubkeys || $[92] !== streakOn || $[93] !== userId) {
    t35 = streakOn ? <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="portfolio-theses-streak-row"><div className="min-w-0 md:col-span-2"><PortfolioThesesPanel attributionWalletAddress={multiWalletEnabled ? null : primaryWalletPubkey} className="h-full" /></div><div className="min-w-0 md:col-span-1"><StreakWidget userId={userId} walletPubkeys={realtimeWalletPubkeys} /></div></div> : <PortfolioThesesPanel attributionWalletAddress={multiWalletEnabled ? null : primaryWalletPubkey} />;
    $[89] = multiWalletEnabled;
    $[90] = primaryWalletPubkey;
    $[91] = realtimeWalletPubkeys;
    $[92] = streakOn;
    $[93] = userId;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  const t36 = multiWalletEnabled ? null : primaryWalletPubkey;
  let t37;
  if ($[95] !== isViewerOwn || $[96] !== realtimeGiftUserIds || $[97] !== realtimeWalletPubkeys || $[98] !== t36 || $[99] !== userId) {
    t37 = <ActivityHistoryPanel userId={userId} walletPubkeys={realtimeWalletPubkeys} giftUserIds={realtimeGiftUserIds} isOwn={isViewerOwn} attributionWalletAddress={t36} />;
    $[95] = isViewerOwn;
    $[96] = realtimeGiftUserIds;
    $[97] = realtimeWalletPubkeys;
    $[98] = t36;
    $[99] = userId;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] !== t10 || $[102] !== t25 || $[103] !== t26 || $[104] !== t27 || $[105] !== t28 || $[106] !== t30 || $[107] !== t31 || $[108] !== t33 || $[109] !== t34 || $[110] !== t35 || $[111] !== t37 || $[112] !== t9) {
    t38 = <main className="w-full space-y-4">{t9}{t10}{t25}{t26}{t27}{t28}{t30}{t31}{t33}{t34}{t35}{t37}</main>;
    $[101] = t10;
    $[102] = t25;
    $[103] = t26;
    $[104] = t27;
    $[105] = t28;
    $[106] = t30;
    $[107] = t31;
    $[108] = t33;
    $[109] = t34;
    $[110] = t35;
    $[111] = t37;
    $[112] = t9;
    $[113] = t38;
  } else {
    t38 = $[113];
  }
  return t38;
}

/**
 * Engagement cards (own viewer): Gifts and Referrals share a 50/50 row when
 * both are enabled; either surface remains full-width when enabled alone.
 * Each surface is gated independently. With both OFF this renders null and the
 * section collapses entirely.
 */
function _temp(w) {
  return w.archivedAt === null;
}
function EngagementCards(t0) {
  const $ = _c(8);
  const {
    userId,
    giftsOn,
    referralsOn
  } = t0;
  let t1;
  if ($[0] !== giftsOn) {
    t1 = giftsOn ? <GiftsSummaryCard /> : null;
    $[0] = giftsOn;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const gifts = t1;
  let t2;
  if ($[2] !== referralsOn || $[3] !== userId) {
    t2 = referralsOn ? <ReferralsPanel userId={userId} /> : null;
    $[2] = referralsOn;
    $[3] = userId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const referrals = t2;
  if (gifts !== null && referrals !== null) {
    let t3;
    if ($[5] !== gifts || $[6] !== referrals) {
      t3 = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{gifts}{referrals}</div>;
      $[5] = gifts;
      $[6] = referrals;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  return gifts ?? referrals;
}

/**
 * Activity history surface (spec 083) — wraps the activity feed (or empty
 * state) inside a labelled `<Panel>` so it visually matches the Theses
 * panel at the bottom of the scroll (after Gifts/Referrals for own viewers).
 * The empty state
 * has its own internal Panel and is rendered unframed via the surrounding
 * panel header.
 */
function ActivityHistoryPanel(t0) {
  const $ = _c(22);
  const {
    userId,
    walletPubkeys,
    giftUserIds,
    isOwn,
    attributionWalletAddress
  } = t0;
  const t = useTranslations("profile.stats");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...ACTIVITY_FEED_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== giftUserIds || $[2] !== userId || $[3] !== walletPubkeys) {
    t2 = {
      userId,
      walletPubkeys,
      giftUserIds,
      fields: t1
    };
    $[1] = giftUserIds;
    $[2] = userId;
    $[3] = walletPubkeys;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activityQuery = useGetUserActivityInfinite(t2);
  let t3;
  if ($[5] !== activityQuery.rows || $[6] !== attributionWalletAddress) {
    t3 = filterActivityRows(activityQuery.rows, attributionWalletAddress);
    $[5] = activityQuery.rows;
    $[6] = attributionWalletAddress;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const attributedRows = t3;
  const hasVisibleRows = attributionWalletAddress === null ? activityQuery.ids.length > 0 : attributedRows.length > 0;
  const isEmpty = !activityQuery.isPending && !activityQuery.isError && !hasVisibleRows && activityQuery.hasNextPage !== true;
  let t4;
  if ($[8] !== t) {
    t4 = t("activity_title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <PortfolioPanelHeader title={t4} />;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== attributionWalletAddress || $[13] !== giftUserIds || $[14] !== isEmpty || $[15] !== isOwn || $[16] !== userId || $[17] !== walletPubkeys) {
    t6 = isEmpty ? <ActivityEmpty /> : <PortfolioActivityFeed userId={userId} isOwn={isOwn} walletPubkeys={walletPubkeys} giftUserIds={giftUserIds} attributionWalletAddress={attributionWalletAddress} />;
    $[12] = attributionWalletAddress;
    $[13] = giftUserIds;
    $[14] = isEmpty;
    $[15] = isOwn;
    $[16] = userId;
    $[17] = walletPubkeys;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <Panel className="min-w-0"><PanelContent className="flex flex-col gap-4">{t5}{t6}</PanelContent></Panel>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function filterActivityRows(rows: readonly GetUserActivityRow[], attributionWalletAddress: string | null): readonly GetUserActivityRow[] {
  if (attributionWalletAddress === null) return rows;
  return rows.filter(row => isActivityAttributedToWallet(row, attributionWalletAddress));
}